import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ConfigResource } from '@df/catalog/config/config.resource';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { ImageModule } from '@df/module-image/image.module';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { CMS_CONFIG_CHECKOUT, IICmsConfigCheckoutData } from '../cms/config/checkout.cms-config';
import { CheckboxComponent } from '../ui/form/checkbox';
import { ResultComponent } from '../ui/resut/result.component';
import { CheckoutFormComponent } from './checkout-form.component';
declare module '@df/catalog/product/product' {
    interface IProductDataV3 {
        donation_link?: string;
        donation_purpose?: string;
        donation_purpose_name?: string;
    }
}
export interface ICheckoutAddDonationContentData {
    productId?: number;
    enable?: boolean;
}
export type ICheckoutAddDonationData = Catalog.IBlockData<ICheckoutAddDonationContentData>;
export const CHECKOUT_ADD_DONATION_CONTENT_BLOCK_URL = 'checkout-add-donation';
@Component({
    selector: 'checkout-add-donation',
    templateUrl: './checkout-add-donation.html',
    standalone: true,
    imports: [ImageModule, TranslationModule, PriceComponent, FormsModule, CheckboxComponent, NgClass, ResultComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutAddDonationComponent implements OnInit, OnDestroy {
    private _donation = false;
    status = new Status();
    open = false;
    product?: Product<IProductDataV3>;
    private subscriptions = new Subscription();
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private app: App,
        private configResource: ConfigResource,
        private checkoutForm: CheckoutFormComponent
    ) {}
    ngOnInit(): void {
        this.configResource.loadModelByUrl(CMS_CONFIG_CHECKOUT).then(() => {
            const config = this.configResource.getModelByUrl(CMS_CONFIG_CHECKOUT) as IICmsConfigCheckoutData;
            const donationId = config?.data?.content?.donationProductId;
            if (donationId) {
                this.app.request.send(
                    undefined,
                    () => this.app.resources.product.load(donationId, 3),
                    () => {
                        const product = this.app.resources.product.getExisting(donationId) as Product<IProductDataV3>;
                        if (product && product.isSalable()) {
                            this.product = product;
                            this.subscriptions.add(
                                this.app.customer.basket.subscribe(() => {
                                    this.onBasketChange();
                                })
                            );
                            this.subscriptions.add(this.checkoutForm.status.subscribe(() => this.detectChanges()));
                            this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
                            this.onBasketChange();
                        }
                    }
                );
            }
        });
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    private onBasketChange(): void {
        this._donation = !!this.getDonationBasketItem();
        this.detectChanges();
    }
    get donation(): boolean {
        return this._donation;
    }
    set donation(value: boolean) {
        if (!this.isBusy && this.product) {
            this._donation = value;
            const basketItem = this.getDonationBasketItem();
            if (value !== !!basketItem) {
                this.checkoutForm.status.busy = true;
                this.app.request
                    .send(this.status, () => {
                        return new Promise((resolve, reject) => {
                            if (basketItem) {
                                basketItem.remove(undefined, resolve, reject, true);
                            } else {
                                this.app.customer.basket.add(undefined, this.product.id, resolve, reject);
                            }
                        });
                    })
                    .then(() => {
                        this.checkoutForm.status.busy = false;
                    });
            }
        }
    }
    get display(): boolean {
        return !!this.product;
    }
    get donationMoreInfoUrl(): string | undefined {
        return this.product?.data?.donation_link;
    }
    get donationDescription(): string | undefined {
        return this.product?.data?.description;
    }
    get donationPurpose(): string | undefined {
        return this.product?.data?.donation_purpose;
    }
    get donationPurposeName(): string | undefined {
        return this.product?.data?.donation_purpose_name;
    }
    get donationPrice(): number | undefined {
        return this.product?.data?.price;
    }
    get donationBaseImage(): string | undefined {
        return this.product?.data?.image;
    }
    get donationExtraImage(): string | undefined {
        if (this.product?.data?.media) {
            return filterMediaByLabel(this.product.data.media, 'base', true)?.[0]?.image;
        }
        return undefined;
    }
    private getDonationBasketItem(): BasketItem | undefined {
        if (this.product) {
            return this.app.customer.basket.byProduct(this.product.id);
        }
    }
    toggle(): void {
        this.open = !this.open;
        this.detectChanges();
    }
    private detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
    get isBusy(): boolean {
        return this.status.busy || this.checkoutForm.status.busy;
    }
}
